
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Award, 
  Search, 
  Download, 
  Eye, 
  Shield, 
  Users,
  TrendingUp,
  Filter
} from 'lucide-react';
import { useStaffCapabilities } from '@/hooks/auth/useStaffCapabilities';
import StaffCertificateAnalytics from './StaffCertificateAnalytics';
import StaffCertificateActions from './StaffCertificateActions';

const StaffCertificateManagement: React.FC = () => {
  const { 
    hasStaffAccess, 
    getStaffCertificates, 
    verifyCertificate, 
    downloadCertificate,
    loading 
  } = useStaffCapabilities();
  
  const [certificates, setCertificates] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  // Load certificates on mount
  useEffect(() => {
    if (hasStaffAccess) {
      loadCertificates();
    }
  }, [hasStaffAccess]);

  const loadCertificates = async () => {
    try {
      const data = await getStaffCertificates();
      setCertificates(data);
    } catch (error) {
      console.error('Error loading certificates:', error);
    }
  };

  if (!hasStaffAccess) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">Access Denied</h3>
          <p className="text-muted-foreground">You need Staff access to manage certificates.</p>
        </CardContent>
      </Card>
    );
  }

  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = cert.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         cert.course_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         cert.certificate_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCourse = selectedCourseFilter === 'all' || cert.course_id === selectedCourseFilter;
    return matchesSearch && matchesCourse;
  });

  const uniqueCourses = Array.from(new Set(certificates.map(cert => cert.course_id)));

  const handleVerifyCertificate = async (certificateId: string) => {
    const success = await verifyCertificate(certificateId);
    if (success) {
      await loadCertificates(); // Refresh the certificates list
    }
  };

  const handleDownloadCertificate = async (certificateId: string) => {
    await downloadCertificate(certificateId);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Award className="h-6 w-6" />
            Certificate Management
          </h2>
          <p className="text-muted-foreground">Manage and track certificates for your users</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {certificates.length} Certificates
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="actions" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Bulk Actions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Search and Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by user name, course, or certificate number..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <select
                    value={selectedCourseFilter}
                    onChange={(e) => setSelectedCourseFilter(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="all">All Courses</option>
                    {uniqueCourses.map(courseId => (
                      <option key={courseId} value={courseId}>
                        {courseId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Certificates List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCertificates.map(certificate => (
              <Card key={certificate.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{certificate.user_name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{certificate.course_title}</p>
                    </div>
                    <Badge variant={certificate.is_verified ? "default" : "secondary"}>
                      {certificate.is_verified ? "Verified" : "Pending"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Certificate #:</span>
                      <span className="font-mono">{certificate.certificate_number}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Issue Date:</span>
                      <span>{new Date(certificate.issue_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Grade:</span>
                      <span className="font-medium">{certificate.metadata?.grade || 'N/A'}%</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleVerifyCertificate(certificate.id)}
                      disabled={certificate.is_verified}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      {certificate.is_verified ? 'Verified' : 'Verify'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleDownloadCertificate(certificate.id)}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredCertificates.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <Award className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">No certificates found</h3>
                <p className="text-muted-foreground">
                  {searchQuery || selectedCourseFilter !== 'all' 
                    ? "No certificates match your search criteria."
                    : "No certificates have been issued yet."
                  }
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="analytics">
          <StaffCertificateAnalytics certificates={certificates} />
        </TabsContent>

        <TabsContent value="actions">
          <StaffCertificateActions certificates={certificates} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StaffCertificateManagement;
